import { Button } from '@/components/shared/buttons/button';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';
import { removeP } from '@/components/shared/utility/format';
import { PartsSymptomsTableProps } from '@/types/shared/parts';

export interface SymptomsTableProps extends PartsSymptomsTableProps {
  dataTrack?: string;
  dataTest?: string;
  dataTestSection?: string;
}

const TextSection = ({
  title,
  content,
  hasSymptomsRemark = false,
  testId,
}: {
  title: string;
  content: string;
  hasSymptomsRemark?: boolean;
  testId: string;
}) => {
  if (!content) return null;

  return (
    <Box display="grid" alignContent="start" rowGap="2">
      <Text as="h3" size="text-body-1" font="bold">
        {title}
      </Text>
      <Text
        as="div"
        size="text-base-2"
        wrap
        dangerouslySetInnerHTML={{ __html: removeP(content) }}
        has-primary-remark={hasSymptomsRemark}
        data-test={testId}
        css={{
          ul: {
            rowGap: '$space-1',
          },
          'ol > li:not(:first-child)::before': {
            paddingTop: '$space-1',
          },
        }}
      />
    </Box>
  );
};

interface InfoPanelProps {
  description?: string;
  url?: string;
  fileSrc?: string;
  dataTrack: string;
  dataTest: string;
  buttonLabel: string;
  buttonTestId: string;
}

const InfoPanel = ({
  description,
  url,
  fileSrc,
  dataTrack,
  dataTest,
  buttonLabel,
  buttonTestId,
}: InfoPanelProps) => {
  if (!description) return null;

  return (
    <Box
      backgroundColor="gray200"
      padding="4"
      display="grid"
      alignContent="start"
      justifyItems={{ '@initial': 'center', '@lg': 'start' }}
      rowGap="3"
    >
      <Text
        as="div"
        size="text-base-2"
        wrap
        font="bold"
        dangerouslySetInnerHTML={{ __html: description }}
        data-test={`${dataTest}_description`}
      />
      {!!(url || fileSrc) && (
        <Button
          label={buttonLabel}
          href={fileSrc || url}
          target={
            fileSrc || (url && /^(https?:\/\/)/.test(url))
              ? '_blank'
              : undefined
          }
          variant="button-gray"
          border
          size="sm"
          tracking={{
            dataTrack,
            dataTrackSection: 'parts-detail',
            dataTrackText: buttonLabel,
            dataTrackUrl: fileSrc || url,
          }}
          test={{ dataTest: `${buttonTestId}_button` }}
        />
      )}
    </Box>
  );
};

const DamagesPanel = ({
  content,
  dataTest,
}: {
  content?: string;
  dataTest: string;
}) => {
  if (!content) return null;

  return (
    <Box
      backgroundColor="primary300"
      padding="4"
      display="grid"
      alignContent="start"
      justifyItems="start"
      rowGap="2"
    >
      <Text
        as="div"
        size="text-base-2"
        wrap
        dangerouslySetInnerHTML={{ __html: removeP(content) }}
        data-test={`${dataTest}_description`}
      />
    </Box>
  );
};

export const SymptomsTable = ({
  isuzuSvcAdvantages,
  symptomsChecker,
  hasSymptomsRemark = false,
  damages,
  damagesRemark,
  warranty,
  offer,
  downloadBrochureButton,
  dataTrack = 'parts-detail-cta',
  dataTest = 'partDetail',
  dataTestSection = 'section_symptoms_table',
}: SymptomsTableProps) => {
  const hasLeftColumn = isuzuSvcAdvantages || symptomsChecker;
  const hasRightColumn =
    damages ||
    damagesRemark ||
    warranty.warrantyDescription ||
    offer?.description;

  return (
    <Section
      size="2xl"
      test={dataTestSection}
      css={{
        paddingTop: '$space-8',
        '@sm': {
          paddingTop: '$space-10',
        },
        '@md': {
          paddingTop: '$space-14',
        },
      }}
    >
      <Container>
        <Box
          position="relative"
          display="grid"
          alignContent="start"
          rowGap="4"
          columnGap="6"
          css={{
            '@lg':
              hasLeftColumn && hasRightColumn
                ? { gridTemplateColumns: 'repeat(2, 1fr)' }
                : { gridTemplateColumns: '1fr' },
          }}
        >
          {!!hasLeftColumn && (
            <Box
              position="relative"
              display="grid"
              alignContent="start"
              rowGap="4"
            >
              <TextSection
                title="ประโยชน์จากการเลือกใช้ที่ศูนย์อีซูซุ"
                content={isuzuSvcAdvantages}
                testId={`${dataTest}_svc_advantages`}
              />
              <TextSection
                title="จุดสังเกตเบื้องต้นของการเสื่อมสภาพ"
                content={symptomsChecker?.replace(
                  'หากไม่เปลี่ยน:',
                  '<strong>หากไม่เปลี่ยน:</strong>'
                )}
                hasSymptomsRemark={hasSymptomsRemark}
                testId={`${dataTest}_symptoms`}
              />
            </Box>
          )}
          {!!hasRightColumn && (
            <Box
              position="relative"
              display="grid"
              alignContent="start"
              rowGap="4"
            >
              <TextSection
                title="โอกาสเสียหายจากการใช้อะไหล่ปลอม/อะไหล่เทียบ"
                content={damages}
                testId={`${dataTest}_damages`}
              />
              <DamagesPanel
                content={damagesRemark}
                dataTest={`${dataTest}_damages`}
              />
              <InfoPanel
                description={warranty.warrantyDescription}
                fileSrc={warranty.warrantyFile?.src}
                url={warranty.warrantyLink}
                dataTrack={dataTrack}
                dataTest={`${dataTest}_warranty`}
                buttonLabel="อ่านรายละเอียดเพิ่มเติม"
                buttonTestId={`${dataTest}_warranty`}
              />
              <InfoPanel
                description={offer?.description}
                url={`/${offer?.url}`}
                dataTrack={dataTrack}
                dataTest={`${dataTest}_offer`}
                buttonLabel="ดูรายละเอียดโปรโมชัน"
                buttonTestId={`${dataTest}_offer`}
              />
            </Box>
          )}
        </Box>
        {!!downloadBrochureButton && (
          <Box textAlign="center" marginTop={{ '@initial': '8', '@sm': '10' }}>
            <Button
              icon={downloadBrochureButton.icon}
              label={downloadBrochureButton.label}
              href={downloadBrochureButton.url}
              target={downloadBrochureButton.target}
              variant={downloadBrochureButton.color}
              border={downloadBrochureButton.type === 'border'}
              isMinWidth={downloadBrochureButton.isMinWidth}
              tracking={{
                dataTrack,
                dataTrackSection: 'parts-detail',
                dataTrackText: downloadBrochureButton.label,
                dataTrackUrl: downloadBrochureButton.url,
              }}
              test={{
                dataTest: `${dataTest}_download_button`,
              }}
            />
          </Box>
        )}
      </Container>
    </Section>
  );
};

SymptomsTable.displayName = 'SectionPartDetailSymptomsTable';
